import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogComponent } from '../dialog/dialog.component';
import { Dialog, IDialogDataWithTicketCategory } from '../dialog';
import { ButtonComponent } from '../../feedback/button/button.component';
import {
  EEntityType,
  ERelationType,
  ETicketType,
  ISortGroupWithTicketData,
} from '@web/dtos';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { ESortOrder } from '../../data-display/table/table.component';
import { CheckboxComponent } from '../../data-input/form-inputs/checkbox/checkbox.component';
import { CmsTicketCategoryService } from '../../../services/cms-ticket-category/cms-ticket-category.service';

@Component({
  selector: 'aw-ticket-category-add-dialog',
  imports: [
    CommonModule,
    DialogComponent,
    ButtonComponent,
    ReactiveFormsModule,
    AppendTestIdPipe,
    CheckboxComponent,
  ],
  templateUrl: './ticket-category-multi-select-dialog.component.html',
})
export class TicketCategoryMultiSelectDialogComponent extends Dialog<IDialogDataWithTicketCategory> {
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  public searchControl: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
  });
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly cmsTicketCategoryService: CmsTicketCategoryService = inject(
    CmsTicketCategoryService
  );
  public fb: FormBuilder = inject(FormBuilder);
  public sortState: ESortOrder | undefined = undefined;
  protected readonly SortOrder: typeof ESortOrder = ESortOrder;
  public visibleTickets: ISortGroupWithTicketData[] = [];
  public unassignedTickets: ISortGroupWithTicketData[] = [];
  public allChecked: FormControl<boolean> = new FormControl<boolean>(false, {
    nonNullable: true,
  });
  public ticketSelectionForm: FormGroup<Record<string, FormControl<boolean>>> =
    new FormGroup<Record<string, FormControl<boolean>>>({});

  constructor() {
    super();
    effect((): void => {
      this.unassignedTickets =
        this.data.ticketCategory.ticketType === ETicketType.Relation
          ? this.cmsTicketCategoryService.unassignedRelationTicketGroups()
          : this.cmsTicketCategoryService.unassignedFixedPriceTicketGroups();
      this.visibleTickets = this.unassignedTickets;
    });

    effect((): void => {
      this.createFormForEveryTicket();
    });

    this.searchControl.valueChanges.subscribe(() => {
      this.updateVisibleTickets();
    });

    this.allChecked.valueChanges.subscribe((value: boolean) => {
      this.ticketSelectionForm.patchValue({
        ...Object.fromEntries(
          this.unassignedTickets.map((ticket: ISortGroupWithTicketData) => [
            this.getTicketSelectionKey(ticket),
            value,
          ])
        ),
      });
    });
  }

  public toggleSort(): void {
    if (this.sortState === undefined) {
      this.sortState = ESortOrder.ASC;
    } else if (this.sortState === ESortOrder.ASC) {
      this.sortState = ESortOrder.DESC;
    } else {
      this.sortState = undefined;
    }
    this.updateVisibleTickets();
  }

  private updateVisibleTickets(): void {
    const search: string = this.searchControl.value.trim().toLowerCase();

    let visibleTickets: ISortGroupWithTicketData[] =
      this.unassignedTickets.filter(
        (ticket: ISortGroupWithTicketData): boolean =>
          ticket.name.toLowerCase().includes(search)
      );

    if (this.sortState === ESortOrder.ASC) {
      visibleTickets = [...visibleTickets].sort((a, b) =>
        a.name.localeCompare(b.name, 'de')
      );
    } else if (this.sortState === ESortOrder.DESC) {
      visibleTickets = [...visibleTickets].sort((a, b) =>
        b.name.localeCompare(a.name, 'de')
      );
    }
    this.visibleTickets = visibleTickets;
  }

  public createFormForEveryTicket(): void {
    const controls: Record<string, FormControl<boolean>> = {};

    this.unassignedTickets.forEach((ticket: ISortGroupWithTicketData) => {
      if (!ticket.name) {
        return;
      }
      controls[this.getTicketSelectionKey(ticket)] = new FormControl<boolean>(
        false,
        {
          nonNullable: true,
        }
      );
    });
    this.ticketSelectionForm = this.fb.group(controls);
  }

  public save(): void {
    const rawValue = this.ticketSelectionForm.getRawValue();
    const checkedTicketGroups: ISortGroupWithTicketData[] =
      this.unassignedTickets.filter(
        (ticket: ISortGroupWithTicketData): boolean =>
          rawValue[this.getTicketSelectionKey(ticket)]
      );
    const currentCategoryId: string | undefined = this.data.ticketCategory.id;

    if (currentCategoryId && checkedTicketGroups.length > 0) {
      this.cmsTicketCategoryService.assignTicketsToCategory(
        checkedTicketGroups,
        currentCategoryId
      );
    }

    this.dialogRef.close();
  }

  public getTicketSelectionKey(ticket: ISortGroupWithTicketData): string {
    return `ticket-group-${this.unassignedTickets.indexOf(ticket)}`;
  }
}
